import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, FormControl, FormLabel, Radio, RadioGroup, VStack } from '@chakra-ui/react';
import { SelectableCard } from '@netiva/classifieds-ui';
import { useAppDispatch, useAppSelector } from '@/store';
import { adActions } from '@/store/ad';
import { useAd } from '@/pages/Ad/hooks';
import { AdComponentProps } from '@/pages/Ad/types';
import { CheckboxInput } from '../AttributeFormControl/components/CheckboxInput';
import { NumberInput } from '../AttributeFormControl/components/NumberInput';
import { SelectionInput } from '../AttributeFormControl/components/SelectionInput';

type PriceMode = 'fixed' | 'onRequest' | 'free';

// shared by the option tiles and the price fields, so both rows line up as columns
const columnWidth = '12rem';

const isTrue = (value?: string | null) => value?.toLowerCase() === 'true';

const fallbackCurrencyKey = 'CHF';

export const PriceComponent: FC<AdComponentProps> = ({ attribute, members = [] }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { formData } = useAppSelector((state) => state.ad);
  const { isLoading } = useAd();
  const priceAttr = members.find((m) => m.key === 'Price');
  const currencyAttr = members.find((m) => m.key === 'Currency');
  const priceNegotiableAttr = members.find((m) => m.key === 'PriceNegotiable');
  const priceOnRequestAttr = members.find((m) => m.key === 'PriceOnRequest');

  const allowForFree = isTrue(attribute?.settings.AllowForFree) && !!priceAttr;
  const allowPriceOnRequest = isTrue(attribute?.settings.AllowPriceOnRequest) && !!priceOnRequestAttr;
  const allowForeignCurrencies = isTrue(attribute?.settings.AllowForeignCurrencies);
  const allowPriceNegotiable = isTrue(attribute?.settings.AllowPriceNegotiable);

  // both "free" and "on request" store a price of 0, as the price is mandatory in the backend; "on request" is
  // told apart by the PriceOnRequest flag, so it has to be checked first
  const [mode, setMode] = useState<PriceMode>(() => {
    if (allowPriceOnRequest && priceOnRequestAttr && isTrue(formData[priceOnRequestAttr.id]?.value)) return 'onRequest';
    if (allowForFree && priceAttr && formData[priceAttr.id]?.value === '0') return 'free';
    return 'fixed';
  });
  // the amount entered for a fixed price is restored when switching back from "free" or "on request"
  const [savedAmount, setSavedAmount] = useState<string | undefined>(() =>
    priceAttr && mode === 'fixed' ? formData[priceAttr.id]?.value : undefined
  );

  const options: { value: PriceMode; label: string }[] = [
    { value: 'fixed', label: t('ad.steps.attributes.price.fixedPrice') },
    ...(allowPriceOnRequest ? [{ value: 'onRequest' as const, label: t('price.onRequest') }] : []),
    ...(allowForFree ? [{ value: 'free' as const, label: t('ad.steps.attributes.price.freeOfCharge') }] : []),
  ];

  const isFixed = mode === 'fixed';

  // the currency is mandatory but hidden for "free", "on request" and platforms without foreign currencies, so it is
  // set here whatever is shown; this also covers existing ads that never had one
  const currencyId = currencyAttr?.id;
  const currencyValue = currencyId ? formData[currencyId] : undefined;
  const hasCurrency = !!currencyValue?.entries?.length;
  const defaultCurrencyEntryId = currencyAttr
    ? (isTrue(currencyAttr.settings.FirstEntryAsSelectDefault)
        ? currencyAttr.items[0]
        : currencyAttr.items.find((item) => item.key?.toUpperCase() === fallbackCurrencyKey)
      )?.id
    : undefined;

  // currencyValue is a dependency although the effect only reads hasCurrency: loading the stored values replaces it
  // with an empty one in the same commit, which leaves hasCurrency unchanged and would not re-run the effect
  useEffect(() => {
    if (!isLoading && currencyId && !hasCurrency && defaultCurrencyEntryId !== undefined) {
      dispatch(adActions.updateFormData({ key: currencyId, entries: [defaultCurrencyEntryId] }));
    }
  }, [currencyId, currencyValue, defaultCurrencyEntryId, dispatch, hasCurrency, isLoading]);

  const handleModeChange = (value: string) => {
    const newMode = value as PriceMode;
    if (priceAttr) {
      if (isFixed) setSavedAmount(formData[priceAttr.id]?.value);
      const amount = newMode === 'fixed' ? savedAmount : '0';
      dispatch(adActions.updateFormData({ key: priceAttr.id, value: amount }));
      // only fixedprice is negotiable
      if (priceNegotiableAttr && newMode !== 'fixed') {
        dispatch(adActions.updateFormData({ key: priceNegotiableAttr.id, value: 'false' }));
      }
    }
    if (priceOnRequestAttr) {
      dispatch(
        adActions.updateFormData({ key: priceOnRequestAttr.id, value: newMode === 'onRequest' ? 'true' : 'false' })
      );
    }
    setMode(newMode);
  };

  return (
    <VStack align="stretch" spacing={4}>
      {options.length > 1 && (
        <RadioGroup value={mode} onChange={handleModeChange}>
          <Flex direction={{ base: 'column', md: 'row' }} gap={2}>
            {options.map((option) => (
              <SelectableCard
                key={option.value}
                isSelected={mode === option.value}
                width={{ base: 'full', md: columnWidth }}
              >
                {/* the negative margin and the extra width cancel the card's theme padding of 1rem, so the whole card
                    stays clickable at input height */}
                <Radio value={option.value} width="calc(100% + 2rem)" m={-4} px={3} py={2}>
                  {option.label}
                </Radio>
              </SelectableCard>
            ))}
          </Flex>
        </RadioGroup>
      )}

      {isFixed && (
        <Flex gap={2} align="flex-end" wrap="wrap">
          {allowForeignCurrencies && currencyAttr && (
            <FormControl isRequired={currencyAttr.mandatory} width={columnWidth}>
              <FormLabel>{currencyAttr.name}</FormLabel>
              <SelectionInput attribute={currencyAttr} />
            </FormControl>
          )}
          {priceAttr && (
            <FormControl isRequired={priceAttr.mandatory} width={columnWidth}>
              <FormLabel>{priceAttr.name}</FormLabel>
              <NumberInput attribute={priceAttr} />
            </FormControl>
          )}
          {allowPriceNegotiable && priceNegotiableAttr && (
            // input height, so the checkbox is centered on the inputs rather than on their labels; the margin matches
            // the tiles' padding, which lines the checkbox up with the radio of the tile above it
            <Flex height={10} align="center" ml={3}>
              <CheckboxInput id={priceNegotiableAttr.id} name={priceNegotiableAttr.name || t('price.onRequest')} />
            </Flex>
          )}
        </Flex>
      )}
    </VStack>
  );
};
